import type { AnyAction } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { deepCopy } from '../../../../../shared/utils';
import { reducerWrapper } from '../../support/utils';
import type { QuickLinkCreatorReducerType } from './types/QuickLinkCreatorReducerType';
import {
  CLEAR_QUICK_LINK,
  UPDATE_IS_GENERAL_STEP_VISITED,
  UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
  UPDATE_IS_LINK_DATA_STEP_VISITED,
  UPDATE_IS_REDIRECTS_STEP_VISITED,
  UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED,
  UPDATE_SELECTED_TEMPLATE,
  FETCH_QUICK_LINK_TEMPLATE,
  FETCH_QUICK_LINK_COLLECTIONS,
  UPDATE_TITLE,
  UPDATE_ALIAS,
  UPDATE_ORIGINAL_WEB_URL,
  UPDATE_FEATURE,
  UPDATE_CHANNEL,
  UPDATE_CAMPAIGN,
  UPDATE_TAGS,
  UPDATE_IOS_REDIRECT,
  UPDATE_ANDROID_REDIRECT,
  UPDATE_DESKTOP_REDIRECT,
  UPDATE_IOS,
  UPDATE_ANDROID,
  UPDATE_DESKTOP,
  UPDATE_WEB_ONLY_LINK,
  SEARCH_IOS_APP_NAME,
  SEARCH_ANDROID_APP_NAME,
  UPDATE_LINK_DATA,
  UPDATE_IS_BLACKLIST_ERROR,
  UPDATE_SOCIAL_TITLE,
  UPDATE_SOCIAL_DESCRIPTION,
  UPDATE_SOCIAL_IMAGE,
  CREATE_QUICK_LINK,
  UPDATE_SELECTED_QUICK_LINK,
  UPDATE_IS_EDITING,
  UPDATE_EDITED_QUICK_LINK,
} from '../../actions/QuickLinkCreator/QuickLinkCreatorActions';

export const INIT_STATE: QuickLinkCreatorReducerType = {
  isGeneralStepVisited: false,
  isAnalyticsTagsStepVisited: false,
  isRedirectsStepVisited: false,
  isLinkDataStepVisited: false,
  isSocialMediaStepVisited: false,
  selectedTemplate: undefined,
  title: undefined,
  quickLinkCollections: undefined,
  alias: undefined,
  originalWebUrl: undefined,
  feature: undefined,
  channel: undefined,
  campaign: undefined,
  tags: [],
  iosRedirect: 'default',
  androidRedirect: 'default',
  desktopRedirect: 'default',
  ios: undefined,
  android: undefined,
  desktop: undefined,
  webOnlyLink: false,
  iosAppName: undefined,
  androidAppName: undefined,
  linkData: [{ id: uuidv4(), key: undefined, value: undefined }],
  isBlacklistError: false,
  socialTitle: undefined,
  socialDescription: undefined,
  socialImage: undefined,
  createdQuickLink: undefined,
  selectedQuickLink: undefined,
  isEditing: false,
  editedQuickLink: undefined,
};

const Handler = (state: QuickLinkCreatorReducerType, action: AnyAction): QuickLinkCreatorReducerType => {
  switch (action.type) {
    case CLEAR_QUICK_LINK.response:
      return { ...deepCopy(INIT_STATE) };
    case UPDATE_IS_GENERAL_STEP_VISITED.response:
      return {
        ...state,
        isGeneralStepVisited: action.data?.isGeneralStepVisited,
      };
    case UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED.response:
      return {
        ...state,
        isAnalyticsTagsStepVisited: action.data?.isAnalyticsTagsStepVisited,
      };
    case UPDATE_IS_REDIRECTS_STEP_VISITED.response:
      return {
        ...state,
        isRedirectsStepVisited: action.data?.isRedirectsStepVisited,
      };
    case UPDATE_IS_LINK_DATA_STEP_VISITED.response:
      return {
        ...state,
        isLinkDataStepVisited: action.data?.isLinkDataStepVisited,
      };
    case UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED.response:
      return {
        ...state,
        isSocialMediaStepVisited: action.data?.isSocialMediaStepVisited,
      };
    case UPDATE_SELECTED_TEMPLATE.response:
      return {
        ...state,
        selectedTemplate: action.data?.selectedTemplate,
      };
    case UPDATE_TITLE.response:
      return {
        ...state,
        title: action.data?.title,
      };
    case FETCH_QUICK_LINK_COLLECTIONS.response:
      return {
        ...state,
        quickLinkCollections: action.data?.quickLinkCollections,
      };
    case UPDATE_ALIAS.response:
      return {
        ...state,
        alias: action.data?.alias,
      };
    case UPDATE_ORIGINAL_WEB_URL.response:
      return {
        ...state,
        originalWebUrl: action.data?.originalWebUrl,
      };
    case UPDATE_FEATURE.response:
      return {
        ...state,
        feature: action.data?.feature,
      };
    case UPDATE_CHANNEL.response:
      return {
        ...state,
        channel: action.data?.channel,
      };
    case UPDATE_CAMPAIGN.response:
      return {
        ...state,
        campaign: action.data?.campaign,
      };
    case UPDATE_TAGS.response:
      return {
        ...state,
        tags: action.data?.tags,
      };
    case UPDATE_IOS_REDIRECT.response:
      return {
        ...state,
        iosRedirect: action.data?.iosRedirect,
      };
    case UPDATE_ANDROID_REDIRECT.response:
      return {
        ...state,
        androidRedirect: action.data?.androidRedirect,
      };
    case UPDATE_DESKTOP_REDIRECT.response:
      return {
        ...state,
        desktopRedirect: action.data?.desktopRedirect,
      };
    case UPDATE_IOS.response:
      return {
        ...state,
        ios: action.data?.ios,
      };
    case UPDATE_ANDROID.response:
      return {
        ...state,
        android: action.data?.android,
      };
    case UPDATE_DESKTOP.response:
      return {
        ...state,
        desktop: action.data?.desktop,
      };
    case UPDATE_WEB_ONLY_LINK.response:
      return {
        ...state,
        webOnlyLink: action.data?.webOnlyLink,
      };
    case SEARCH_IOS_APP_NAME.response:
      return {
        ...state,
        iosAppName: action.data?.iosAppName,
      };
    case SEARCH_ANDROID_APP_NAME.response:
      return {
        ...state,
        androidAppName: action.data?.androidAppName,
      };
    case UPDATE_LINK_DATA.response:
      return {
        ...state,
        linkData: action.data?.linkData,
      };
    case UPDATE_IS_BLACKLIST_ERROR.response:
      return {
        ...state,
        isBlacklistError: action.data?.isBlacklistError,
      };
    case UPDATE_SOCIAL_TITLE.response:
      return {
        ...state,
        socialTitle: action.data?.socialTitle,
      };
    case UPDATE_SOCIAL_DESCRIPTION.response:
      return {
        ...state,
        socialDescription: action.data?.socialDescription,
      };
    case UPDATE_SOCIAL_IMAGE.response:
      return {
        ...state,
        socialImage: action.data?.socialImage,
      };
    case CREATE_QUICK_LINK.response:
      return {
        ...state,
        createdQuickLink: action.data?.createdQuickLink,
      };
    case UPDATE_SELECTED_QUICK_LINK.response:
      return {
        ...state,
        selectedQuickLink: action.data?.selectedQuickLink,
      };
    case UPDATE_IS_EDITING.response:
      return {
        ...state,
        isEditing: action.data?.isEditing,
      };
    case UPDATE_EDITED_QUICK_LINK.response:
      return {
        ...state,
        editedQuickLink: action.data?.editedQuickLink,
      };
    default:
      return state;
  }
};

const TypelessHandler = (state: any, action: any) => {
  return Handler(state, action);
};

const QuickLinkCreatorReducer = reducerWrapper(INIT_STATE, TypelessHandler, {
  request: [
    CLEAR_QUICK_LINK,
    UPDATE_IS_GENERAL_STEP_VISITED,
    UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
    UPDATE_IS_LINK_DATA_STEP_VISITED,
    UPDATE_IS_REDIRECTS_STEP_VISITED,
    UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED,
    UPDATE_SELECTED_TEMPLATE,
    FETCH_QUICK_LINK_TEMPLATE,
    FETCH_QUICK_LINK_COLLECTIONS,
    UPDATE_TITLE,
    UPDATE_ALIAS,
    UPDATE_ORIGINAL_WEB_URL,
    UPDATE_FEATURE,
    UPDATE_CHANNEL,
    UPDATE_CAMPAIGN,
    UPDATE_IOS_REDIRECT,
    UPDATE_ANDROID_REDIRECT,
    UPDATE_DESKTOP_REDIRECT,
    UPDATE_IOS,
    UPDATE_ANDROID,
    UPDATE_DESKTOP,
    UPDATE_WEB_ONLY_LINK,
    SEARCH_IOS_APP_NAME,
    SEARCH_ANDROID_APP_NAME,
    UPDATE_LINK_DATA,
    UPDATE_IS_BLACKLIST_ERROR,
    UPDATE_SOCIAL_TITLE,
    UPDATE_SOCIAL_DESCRIPTION,
    UPDATE_SOCIAL_IMAGE,
    CREATE_QUICK_LINK,
    UPDATE_SELECTED_QUICK_LINK,
    UPDATE_IS_EDITING,
    UPDATE_EDITED_QUICK_LINK,
  ],
  error: [
    CLEAR_QUICK_LINK,
    UPDATE_IS_GENERAL_STEP_VISITED,
    UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
    UPDATE_IS_LINK_DATA_STEP_VISITED,
    UPDATE_IS_REDIRECTS_STEP_VISITED,
    UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED,
    UPDATE_SELECTED_TEMPLATE,
    FETCH_QUICK_LINK_TEMPLATE,
    FETCH_QUICK_LINK_COLLECTIONS,
    UPDATE_TITLE,
    UPDATE_ALIAS,
    UPDATE_ORIGINAL_WEB_URL,
    UPDATE_FEATURE,
    UPDATE_CHANNEL,
    UPDATE_CAMPAIGN,
    UPDATE_IOS_REDIRECT,
    UPDATE_ANDROID_REDIRECT,
    UPDATE_DESKTOP_REDIRECT,
    UPDATE_IOS,
    UPDATE_ANDROID,
    UPDATE_DESKTOP,
    UPDATE_WEB_ONLY_LINK,
    SEARCH_IOS_APP_NAME,
    SEARCH_ANDROID_APP_NAME,
    UPDATE_LINK_DATA,
    UPDATE_IS_BLACKLIST_ERROR,
    UPDATE_SOCIAL_TITLE,
    UPDATE_SOCIAL_DESCRIPTION,
    UPDATE_SOCIAL_IMAGE,
    CREATE_QUICK_LINK,
    UPDATE_SELECTED_QUICK_LINK,
    UPDATE_IS_EDITING,
    UPDATE_EDITED_QUICK_LINK,
  ],
});

export default QuickLinkCreatorReducer;
